import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { InfiniteLoader, List as RVList } from 'react-virtualized';

/**
 * @class List
 * @classdesc Reusable List Component
 * @property {Object} state
 * @property {Object} listRef
 */
export default class List extends Component {
  static propTypes = {
    className: PropTypes.string,
    data: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        className: PropTypes.string,
        style: PropTypes.object,
      }),
    ).isRequired,
    height: PropTypes.number.isRequired,
    rowHeight: PropTypes.number.isRequired,
    isInfinite: PropTypes.bool.isRequired,
    width: PropTypes.number.isRequired,
    customRowRenderer: PropTypes.func,
    getRows: PropTypes.func,
    size: PropTypes.number,
    batch: PropTypes.number,
    totalDataLength: PropTypes.number,
  };

  static defaultProps = {
    className: null,
    batch: 0,
    customRowRenderer: () => {},
    getRows: null,
    size: 50,
    totalDataLength: Number.MAX_SAFE_INTEGER,
  };

  constructor(props) {
    super(props);
    this.listRef = React.createRef();
  }

  componentDidUpdate() {
    this.listRef.current._registeredChild.forceUpdateGrid();
  }

  /**
   * @description Fetch Row Count
   * Row count must be 1 greater than data length to load more rows.
   * ...According to loadMoreRows definition in react-virtualized docs
   * @return {Number}
   */
  getRowCount = () => {
    const { isInfinite, data, totalDataLength } = this.props;
    return isInfinite && data.length < totalDataLength ? data.length + 1 : data.length;
  };

  isRowLoaded = ({ index }) => {
    const { data } = this.props;
    return !!data[index];
  };

  loadMoreRows = () => {
    const { batch, getRows, size, sortBy, sortDirection } = this.props;
    return getRows(batch, size, sortBy, sortDirection);
  };

  /**
   * @description Render Row Function
   * @param {Object} Props
   * @return {ReactElement} DefaultTableRowRenderer
   */
  rowRenderer = props => {
    const { index } = props;
    const { data, customRowRenderer } = this.props;

    if (!this.isRowLoaded({ index })) {
      /* Fixes bug where loading row would appear in the middle of the table after fetching */
      if (props.isScrolling) return null;
      return (<div>Loading</div>);
    }
    const newProps = { ...props, datum: data[index] };
    return customRowRenderer(newProps);
  };

  render() {
    const { width, height, rowHeight, className } = this.props;

    return (
      <div className={className}>
        <InfiniteLoader
          ref={this.listRef}
          isRowLoaded={this.isRowLoaded}
          loadMoreRows={this.loadMoreRows}
          rowCount={this.getRowCount()}>
          {({ onRowsRendered, registerChild }) => (
            <RVList
              ref={registerChild}
              height={height}
              onRowsRendered={onRowsRendered}
              rowCount={this.getRowCount()}
              rowHeight={rowHeight}
              rowRenderer={this.rowRenderer}
              width={width}
            />
          )}
        </InfiniteLoader>
      </div>
    );
  }
}
